import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Auth({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin ? { email: form.email, password: form.password } : form;
      const res = await api.post(url, payload);
      login(res.data.access_token, res.data.user);
      toast.success(isLogin ? "Welcome back" : "Account created");
      const dest = res.data.user.role === "admin" ? "/admin" : (location.state?.from || "/my-bookings");
      navigate(dest);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div className="hidden md:block relative">
        <img src="https://images.unsplash.com/photo-1621269759351-0f3f95239966?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Himalayan valley" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/40" />
        <div className="absolute bottom-10 left-10 right-10">
          <Link to="/" className="font-display text-3xl text-primary-foreground">zatayoo</Link>
          <p className="text-primary-foreground/80 mt-2 max-w-sm">Hidden places, treks and secret coastlines — all your trips, one account.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="md:hidden font-display text-3xl text-foreground block mb-8">zatayoo</Link>
          <h1 className="font-display text-4xl text-foreground">{isLogin ? "Welcome back" : "Create account"}</h1>
          <p className="text-muted-foreground mt-2">{isLogin ? "Sign in to manage your bookings." : "Track and manage all your sessions."}</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {!isLogin && (
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="auth-name"
                className="w-full h-12 rounded-xl border border-input bg-transparent px-4 focus:ring-2 focus:ring-accent focus:outline-none" />
            )}
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="auth-email"
              className="w-full h-12 rounded-xl border border-input bg-transparent px-4 focus:ring-2 focus:ring-accent focus:outline-none" />
            <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" data-testid="auth-password"
              className="w-full h-12 rounded-xl border border-input bg-transparent px-4 focus:ring-2 focus:ring-accent focus:outline-none" />
            <button type="submit" disabled={loading} data-testid="auth-submit"
              className="w-full h-12 rounded-full bg-accent text-accent-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60 hover:-translate-y-px transition-transform">
              {loading && <Loader2 size={16} className="animate-spin" />}
              {isLogin ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {isLogin ? "New to Zatayoo? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-accent font-semibold" data-testid="auth-switch">
              {isLogin ? "Create an account" : "Sign in"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
